import React from 'react';
import { styled } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { authColors, authFontFamily, authType } from 'app/pages/auth/shared/authTheme';

/** Material 3 checkbox + label used for "Remember me" and consent rows. */

const Row = styled('label')({
  display: 'flex',
  // Top-aligned so multi-line labels (SMS consent + fine print) keep the box
  // beside their first line instead of floating mid-block.
  alignItems: 'flex-start',
  gap: 8,
  cursor: 'pointer',
  userSelect: 'none',
});

const HiddenInput = styled('input')({
  position: 'absolute',
  opacity: 0,
  width: 0,
  height: 0,
});

const Box = styled('span')<{ 'data-checked': boolean }>(({ 'data-checked': checked }) => ({
  boxSizing: 'border-box',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  // Never let long labels compress the box, and nudge it onto the label's
  // first text line (20px box vs ~24px line height).
  flexShrink: 0,
  marginTop: 2,
  borderRadius: 4,
  border: checked ? `1.5px solid ${authColors.primary}` : `1.5px solid ${authColors.checkboxBorder}`,
  backgroundColor: checked ? authColors.primary : authColors.white,
  transition: 'all 120ms ease',
  '& svg': { fontSize: 16, color: authColors.white },
}));

const LabelText = styled('span')({
  ...authType.body,
  fontFamily: authFontFamily,
});

export interface AuthCheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: React.ReactNode;
  name?: string;
}

export default function AuthCheckbox({ checked, onChange, label, name }: AuthCheckboxProps) {
  return (
    <Row>
      <HiddenInput
        type="checkbox"
        name={name}
        checked={checked}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
      />
      <Box data-checked={checked}>{checked && <CheckIcon />}</Box>
      <LabelText>{label}</LabelText>
    </Row>
  );
}
